import type { CustomChartSerie } from "@tokenterminal/tt-analytics-api-types/dist/api/customChart"
import { useMemo } from "react"
import { useMetrics } from "../../../data/useMetrics"
import { toDictionary } from "../../../utils/toDictionary"
import type { YAxisOptions } from "../tt-ui-chart/Chart"

/** Maps a serie id to the earlier serie on the same side whose y axis it shares. */
export function getYaxisMergePaths(
  chartSeries: Array<CustomChartSerie>,
  metricConfigurations: Map<string, object>
) {
  const seriesByYAxis: Record<"left" | "right", Array<CustomChartSerie>> = {
    left: [],
    right: [],
  }
  const axisMap = new Map<string, string>()

  for (const chartSerie of chartSeries) {
    if (chartSerie.yAxis === null) {
      continue
    }
    const sameSideSeries = seriesByYAxis[chartSerie.yAxis]

    if (metricConfigurations.has(chartSerie.metric)) {
      const isPercentage = chartSerie.display === "percentage"
      const serieToMergeWith = sameSideSeries.find(
        (serie) =>
          metricConfigurations.has(serie.metric) &&
          (serie.display === "percentage") === isPercentage
      )
      if (serieToMergeWith) {
        axisMap.set(chartSerie.id, serieToMergeWith.id)
      }
    }

    sameSideSeries.push(chartSerie)
  }

  return axisMap
}

/** Highcharts y axes for the chart series. Suspends until the metrics load. */
export function useChartYAxisOptions(
  chartSettings: Array<CustomChartSerie>,
  hideYAxisLabels: boolean
) {
  const metrics = useMetrics()

  return useMemo(() => {
    const metricConfigurations = toDictionary(metrics, "slug")

    const yAxisMap = getYaxisMergePaths(chartSettings, metricConfigurations)

    const yAxisOptions = new Map<string, YAxisOptions>()
    for (const [index, chartSetting] of chartSettings.entries()) {
      if (chartSetting.yAxis === null) {
        yAxisOptions.set(chartSetting.id, {
          visible: false,
        })
        continue
      }

      // const yAxisName = yAxisNames.get(index) ?? new Set([chartSetting.title])
      const defaultYaxis: YAxisOptions = {
        id: chartSetting.id,
        title: {
          text: hideYAxisLabels ? "" : chartSetting.title,
        },
        visible: true,
        opposite: chartSetting.yAxis === "right",
      }

      // label should be comma separated
      if (yAxisMap.size) {
        if (!yAxisMap.has(chartSetting.id)) {
          Object.assign(defaultYaxis, {
            lineWidth: index === 0 ? 0 : 1,
            lineColor: "hsl(var(--border-default))",
          })
          yAxisOptions.set(chartSetting.id, defaultYaxis)
        }
      } else {
        yAxisOptions.set(chartSetting.id, defaultYaxis)
      }
    }

    for (const chartSetting of chartSettings) {
      const mergedAxisId = yAxisMap.get(chartSetting.id)
      const yAxis =
        mergedAxisId === undefined ? undefined : yAxisOptions.get(mergedAxisId)
      if (!yAxis) {
        continue
      }

      const titleChunks = (yAxis.title?.text ?? chartSetting.title).split(", ")
      titleChunks.push(chartSetting.title)
      yAxis.title = {
        ...yAxis.title,
        text: hideYAxisLabels ? "" : titleChunks.join(", "),
      }
    }

    return Array.from(yAxisOptions.values())
  }, [chartSettings, hideYAxisLabels, metrics])
}
